import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { 
  ArrowLeft, Gift, Wallet, Plus, Store, CreditCard, 
  Loader2, CheckCircle, AlertCircle, ChevronRight, RefreshCw, Copy
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface GiftCertificateBalance {
  client_id: string;
  client_name: string;
  available_balance: number;
  checkout_code?: string;
  logo_url?: string | null;
}

interface GiftCertificateBalancesResponse {
  balances: GiftCertificateBalance[];
  total_balance: number;
}

interface RedeemResponse {
  success: boolean;
  amount?: number;
  restaurant_name?: string;
  error?: string;
}

export default function GiftCertificates() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [redeemCode, setRedeemCode] = useState("");
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemSuccess, setRedeemSuccess] = useState<RedeemResponse | null>(null);

  const { data: balancesData, isLoading, refetch } = useQuery<GiftCertificateBalancesResponse>({
    queryKey: ['/api/gift-certificates/balances'],
    retry: false,
  });

  const redeemMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await apiRequest('POST', '/api/gift-certificates/redeem', { code });
      return response.json();
    },
    onSuccess: (data: RedeemResponse) => {
      setRedeemSuccess(data);
      setRedeemCode("");
      queryClient.invalidateQueries({ queryKey: ['/api/gift-certificates/balances'] });
    },
    onError: (error: any) => {
      toast({
        title: "Redemption Failed",
        description: error.message || "Invalid or expired gift certificate code",
        variant: "destructive"
      });
    }
  });

  const handleRedeem = () => {
    if (!redeemCode.trim()) {
      toast({
        title: "Enter Code",
        description: "Please enter a gift certificate code",
        variant: "destructive"
      });
      return;
    }
    redeemMutation.mutate(redeemCode.trim());
  };

  const totalBalance = balancesData?.total_balance || 0;
  const balances = balancesData?.balances || [];

  const redeemCard = (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Have a Gift Certificate?</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Enter your gift certificate code below to add the balance to your account.
        </p>
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              placeholder="Enter code (e.g., GIFT-XXXX-XXXX)"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              className="uppercase"
            />
          </div>
          <Button
            onClick={handleRedeem}
            disabled={redeemMutation.isPending || !redeemCode.trim()}
          >
            {redeemMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Redeem"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-full bg-background">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b">
        <div className="max-w-4xl mx-auto flex items-center gap-3 px-4 py-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setLocation('/profile')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">Gift Certificates</h1>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={cn("h-5 w-5", isLoading && "animate-spin")} />
          </Button>
        </div>
      </header>

      <main className="p-4 pb-24">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Hero balance card — full width on all breakpoints */}
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <Gift className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-orange-100 text-sm">Total Balance</p>
                  <p className="text-3xl font-bold">${totalBalance.toFixed(2)}</p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button 
                  variant="secondary" 
                  className="flex-1 bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={() => setShowRedeemModal(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Redeem Code
                </Button>
                <Button 
                  variant="secondary" 
                  className="flex-1 bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={() => setLocation('/restaurants')}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Buy Gift Card
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Loading — full width on all breakpoints */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Empty state — full width on all breakpoints */}
          {!isLoading && balances.length === 0 && (
            <>
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Gift className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold mb-2">No Gift Certificates Yet</h3>
                  <p className="text-muted-foreground text-sm mb-6">
                    Redeem a gift certificate code or purchase one for your favorite restaurants.
                  </p>
                  <Button onClick={() => setShowRedeemModal(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Redeem a Code
                  </Button>
                </CardContent>
              </Card>
              <Separator className="my-8" />
              {redeemCard}
            </>
          )}

          {/* Two-column grid on lg+ — only when balances are present */}
          {!isLoading && balances.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
              {/* Left column: balances list */}
              <div className="lg:col-span-3 space-y-4">
                <h2 className="font-semibold text-lg">Your Balances by Restaurant</h2>
                {balances.map((balance) => (
                  <Card key={balance.client_id} className="hover-elevate cursor-pointer">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                          {balance.logo_url ? (
                            <img src={balance.logo_url} alt={balance.client_name} className="w-full h-full object-cover" />
                          ) : (
                            <Store className="h-6 w-6 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium truncate">{balance.client_name}</h3>
                          <p className="text-sm text-muted-foreground">Available balance</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-600">
                            ${balance.available_balance.toFixed(2)}
                          </p>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-xs text-muted-foreground"
                            onClick={() => setLocation(`/restaurant/${balance.client_id}`)}
                          >
                            Order Now
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Right column: redeem card — sticky on desktop, stacked on mobile */}
              <div className="lg:col-span-2">
                <Separator className="my-8 lg:hidden" />
                <div className="lg:sticky lg:top-[57px]">
                  {redeemCard}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Dialog open={showRedeemModal} onOpenChange={setShowRedeemModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-orange-500" />
              Redeem Gift Certificate
            </DialogTitle>
            <DialogDescription>
              Enter the gift certificate code you received to add the balance to your account.
            </DialogDescription>
          </DialogHeader>

          {redeemSuccess ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Success!</h3>
              <p className="text-muted-foreground">
                ${redeemSuccess.amount?.toFixed(2)} has been added to your balance
                {redeemSuccess.restaurant_name && ` at ${redeemSuccess.restaurant_name}`}.
              </p>
              <Button 
                className="mt-6" 
                onClick={() => {
                  setRedeemSuccess(null);
                  setShowRedeemModal(false);
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Gift Certificate Code</Label>
                  <Input
                    id="code"
                    placeholder="GIFT-XXXX-XXXX"
                    value={redeemCode}
                    onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                    className="uppercase text-lg tracking-wide"
                    autoComplete="off"
                    disabled={redeemMutation.isPending}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  The code can be found on your gift certificate card or email.
                </p>
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowRedeemModal(false);
                    setRedeemCode("");
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleRedeem}
                  disabled={redeemMutation.isPending || !redeemCode.trim()}
                >
                  {redeemMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Redeeming...
                    </>
                  ) : (
                    <>
                      <Gift className="h-4 w-4 mr-2" />
                      Redeem
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
